import { Stack, useClipboard, useToast } from '@chakra-ui/react';
import { Alert } from '@time-line/components/common';
import {
  Facebook,
  LinkedIn,
  Mail,
  Twitter,
  Url,
} from '@time-line/components/icons';
import { SocialIcon } from 'components/common/SocialIcon';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { getPageUrl } from 'utils/getBaseUrl';

const iconProps = {
  boxSize: 'xs',
  color: 'blackAlpha.700',
  transitionProperty: 'common',
  transitionDuration: 'normal',
};

interface BlogShareIconsProps {
  title: string;
}

export function BlogShareIcons({ title }: BlogShareIconsProps) {
  const router = useRouter();
  const url = getPageUrl(router.asPath);
  const toast = useToast();
  const { onCopy } = useClipboard(url);

  const SHARERS = useMemo(
    () => [
      {
        name: 'facebook',
        icon: <Facebook {...iconProps} />,
        href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        target: '_blank',
      },
      {
        name: 'twitter',
        icon: <Twitter {...iconProps} />,
        href: `https://twitter.com/intent/tweet?url=${url}`,
        target: '_blank',
      },
      {
        name: 'linkedin',
        icon: <LinkedIn {...iconProps} />,
        href: `https://www.linkedin.com/shareArticle?url=${url}`,
        target: '_blank',
      },
      {
        name: 'email',
        icon: <Mail {...iconProps} />,
        href: `mailto:?body=Here’s a cool article on the Timeline blog "${title}" ${url}&subject=Hey, I thought you might be interested in this…`,
      },
      {
        name: 'copy',
        icon: <Url {...iconProps} />,
        onClick: () => {
          onCopy();
          toast({
            position: 'bottom',
            duration: 2000,
            isClosable: true,
            render: ({ onClose }) => (
              <Alert
                variant="solid"
                colorScheme="gray"
                isIconVisible={false}
                onClose={onClose}
                borderRadius={{ base: 'none', sm: 'base' }}
                margin={0}
              >
                Link copied to clipboard.
              </Alert>
            ),
          });
        },
      },
    ],
    [url, toast, onCopy, title],
  );

  return (
    <Stack direction="row" gap="2xs">
      {SHARERS.map((sharer) => (
        <SocialIcon
          as={(!sharer.onClick && 'a') || undefined}
          onClick={sharer.onClick}
          key={sharer.name}
          href={(!sharer.onClick && sharer.href) || undefined}
          aria-label={`Share post via ${sharer.name}`}
          target={sharer.target}
          rel="noreferrer"
        >
          {sharer.icon}
        </SocialIcon>
      ))}
    </Stack>
  );
}
